import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  MyLocation as MyLocationIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

interface RadiusSearchInputProps {
  onSearch: (lat: number, lng: number, radius: number) => void;
  onClear: () => void;
  loading?: boolean;
  clickedLat?: number;
  clickedLng?: number;
  initialRadius?: number;
  onRadiusChange?: (radius: number) => void;
}

const RadiusSearchInput: React.FC<RadiusSearchInputProps> = ({
  onSearch,
  onClear,
  loading,
  clickedLat,
  clickedLng,
  initialRadius = 10,
  onRadiusChange,
}) => {
  const [lat, setLat] = useState<string>("");
  const [lng, setLng] = useState<string>("");
  const [radius, setRadius] = useState<number>(initialRadius);
  const [expanded, setExpanded] = useState<boolean>(true);

  // Update form fields when map is clicked
  useEffect(() => {
    if (clickedLat !== undefined && clickedLng !== undefined) {
      setLat(clickedLat.toFixed(6));
      setLng(clickedLng.toFixed(6));
    }
  }, [clickedLat, clickedLng]);

  // Notify parent when radius changes
  useEffect(() => {
    console.log(`[RADIUS] Radius changed to: ${radius} miles`);
    onRadiusChange?.(radius);
  }, [radius, onRadiusChange]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);

    // Validate inputs
    if (isNaN(latNum) || isNaN(lngNum)) {
      alert("Please enter valid latitude and longitude values");
      return;
    }

    if (latNum < -90 || latNum > 90) {
      alert("Latitude must be between -90 and 90");
      return;
    }

    if (lngNum < -180 || lngNum > 180) {
      alert("Longitude must be between -180 and 180");
      return;
    }

    if (radius < 0 || radius > 5000) {
      alert("Radius must be between 0 and 5000 miles");
      return;
    }

    console.log(`[FORM] Search submitted: lat=${latNum.toFixed(6)}, lng=${lngNum.toFixed(6)}, radius=${radius} miles`);
    onSearch(latNum, lngNum, radius);
  };

  const handleClear = () => {
    console.log(`[FORM] Clearing search form`);
    setLat("");
    setLng("");
    setRadius(10);
    onClear();
  };

  return (
    <Card className="radius-search-card mb-4">
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" gutterBottom>
              Radius Search
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click on the map or enter coordinates to search for events within a radius
            </Typography>
          </Box>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box mt={3}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {/* Latitude */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    name="lat"
                    type="number"
                    value={lat}
                    onChange={(e) => setLat(e.target.value)}
                    placeholder="e.g., 41.8781"
                    size="small"
                    required
                    inputProps={{
                      step: "any",
                      min: -90,
                      max: 90,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MyLocationIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Longitude */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Longitude"
                    name="lng"
                    type="number"
                    value={lng}
                    onChange={(e) => setLng(e.target.value)}
                    placeholder="e.g., -87.6298"
                    size="small"
                    required
                    inputProps={{
                      step: "any",
                      min: -180,
                      max: 180,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MyLocationIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Radius */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Radius"
                    name="radius"
                    type="number"
                    value={radius}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only update if the value is a valid number or is being cleared (empty string)
                      if (value === "") {
                        // Keep the current value, don't allow empty
                        console.log(`[RADIUS] Radius cleared, keeping current value: ${radius}`);
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          setRadius(numValue);
                        }
                      }
                    }}
                    size="small"
                    required
                    inputProps={{
                      step: "0.1",
                      min: 0,
                      max: 5000,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">miles</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<ClearIcon />}
                      onClick={handleClear}
                      disabled={loading}
                    >
                      Clear
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={<SearchIcon />}
                      disabled={loading}
                    >
                      {loading ? "Searching..." : "Search Radius"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default RadiusSearchInput;
